<template>
    <div
        v-if="parameterType"
        class="widget-other-filters"
    >
        <RedesignFormLabel
            :label="templateParameterName"
            :info-text="templateParameterDescription"
            class="mb-2"
        />
        <div class="w-full">
            <component
                :is="currentComponent.component"
                v-model="componentValue"
                :placeholder="$t('settings.add.filter')"
                v-bind="currentComponent.parameterData"
                full-width
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import RedesignFormLabel from '@/components/RedesignBaseComponents/Form/RedesignFormLabel.vue'
import { computed } from 'vue'
import RedesignBaseInput from '@/components/RedesignBaseComponents/Inputs/RedesignBaseInput.vue'
import RedesignInputNumber from '@/components/RedesignBaseComponents/Inputs/RedesignInputNumber.vue'
import RedesignBaseSelect from '@/components/RedesignBaseComponents/Inputs/RedesignBaseSelect.vue'
import FilterRadioGroup from '@/components/RedesignComponents/Widgets/Common/OtherFilters/FilterRadioGroup.vue'
import { ObjectAnyType, WidgetConfig, WidgetParameterValueJson } from '@/types/dashboard'
import cloneDeep from 'lodash/cloneDeep'
import {
    getTemplateParameterHelpDescription,
    getTemplateParameterHelpName
} from '@/components/RedesignComponents/Widgets/widget.helper'
import { parseJsonStringOrReturnString } from '@/util/objectUtil'

const components: Record<number, ObjectAnyType> = {
    1: {
        component: RedesignBaseInput,
        parameterData: {
            name: 'Input',
            type: 'text'
        }
    },
    2: {
        component: RedesignInputNumber,
        parameterData: {
            name: 'Input',
            type: 'number',
            min: 0
        }
    },
    3: {
        component: '',
        parameterData: {
            name: 'Date',
            type: 'date'
        }
    },
    4: {
        component: RedesignInputNumber,
        parameterData: {
            name: 'Input',
            type: 'number'
        }
    },
    5: {
        component: RedesignBaseSelect,
        parameterData: {
            name: 'Select',
            type: 'select',
            multiple: false
        }
    },
    6: {
        component: RedesignBaseSelect,
        parameterData: {
            name: 'AutoComplete',
            type: 'multiple',
            multiple: true
        }
    },
    7: {
        component: FilterRadioGroup,
        parameterData: {
            name: 'RadioGroup',
            type: 'boolean'
        }
    },
    16: {
        component: '',
        parameterData: {
            name: 'MultiSelect',
            type: 'multiple'
        }
    }
}

const props = defineProps<{
    value: WidgetConfig
}>()

const emit = defineEmits([ 'input', 'update:value' ])

/* Computed */
const model = computed({
    get () {
        return props.value
    },
    set (val) {
        emit('input', val)
        emit('update:value', val)
    }
})
const parameterType = computed(() => {
    return model.value.ParameterType
})
const templateParameterName = computed(() => {
    return getTemplateParameterHelpName(model.value)
})
const templateParameterDescription = computed(() => {
    return getTemplateParameterHelpDescription(model.value)
})
const componentValue = computed({
    get () {
        if (model.value.WidgetParameterJson === 1) {
            return model.value.WidgetParameterValueJson
        } else {
            return parseJsonStringOrReturnString(model.value.WidgetParameterValue || '')
        }
    },
    set (val) {
        const updatedModel: Partial<WidgetConfig> = {}

        if (model.value.WidgetParameterJson === 1) {
            updatedModel.WidgetParameterValueJson = cloneDeep(val) as WidgetParameterValueJson
        } else {
            updatedModel.WidgetParameterValue = typeof (val) === 'string' ? val : JSON.stringify(val)
        }

        model.value = {
            ...model.value,
            ...updatedModel
        }
    }
})
const currentComponent = computed(() => {
    if (!parameterType.value || !components[parameterType.value]) {
        return {
            component: '',
            parameterData: {}
        }
    }

    const component = components[parameterType.value]
    const filterParameterData = model.value.ParameterData || {}

    return {
        ...component,
        parameterData: {
            labelKey: 'Key',
            valueKey: 'Value',
            data: filterParameterData.ItemList || [],
            ...component.parameterData,
            ...filterParameterData
        }
    }
})
</script>
